import React, { useEffect, useState } from 'react'
import { Button, ConfirmationModal, Loading } from 'src/components'
import { montaTypeOptions } from 'src/constants/montaType'
import { useStyles } from './styles'
import { DiagnosisModal } from '../components/DiagnosisModal'
import { ReproductiveHistoryModal } from '../components/ReproductiveHistoryModal'
import { useBreedingListDetails } from './hooks/useBreedingListDetails'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { CoverageProcessParams, PregnancyDiagnosisParams } from 'src/domain/coverage-process'
import { useBreedingListDetailsForm } from './hooks/useBreedingListDetailsForm'
import { AnimalInfoSection } from './components/AnimalInfoSection'
import { ReproductionSection } from './components/ReproductionSection'
import { DiagnosisSection } from './components/DiagnosisSection'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BreedingHeader } from './components/BreedingHeader'
import { useHistoryDiagnosisModal } from './hooks/useHistoryDiagnosisModal'
import { NotFoundError } from 'src/utils/NotFoundError'

export type formParams = {
  pregnancyDiagnosis: PregnancyDiagnosisParams
  coverageProcess: CoverageProcessParams
}

export const BreedingListDetails = () => {
  const breedingListDetails = useBreedingListDetails()
  const formMethods = useForm<formParams>()
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()

  const idDiagGest = searchParams.get('idDiagGest')

  if (breedingListDetails.isError) {
    if ((breedingListDetails.error as any)?.status === 404) {
      throw new NotFoundError('Breeding not found')
    }

    navigate('/reproducao/lista-de-reproducao', { state: { message: 'Reprodução não encontrada' } })
  }

  return (
    <div key={idDiagGest}>
      <FormProvider {...formMethods}>
        <BreedingListDetailsComponent breedingListDetails={breedingListDetails} />
      </FormProvider>
    </div>
  )
}

type BreedingListDetailsComponentProps = {
  breedingListDetails: ReturnType<typeof useBreedingListDetails>
}

export const BreedingListDetailsComponent: React.FC<BreedingListDetailsComponentProps> = ({ breedingListDetails }) => {
  const style = useStyles()
  const [showDiagnosisForm, setShowDiagnosisForm] = useState(false)

  const {
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleConfirmDelete,
    isUpdatingCoverageProcess,
    isDeleteModalOpen,
    handleOpenDiagnosisModal,
    handleConfirmOpenDiagnosisModal,
    handleSaveDiagnosis,
    handleSaveDiagnosisComplete,
    handleSave,
    isLoading,
    pregnancyDiagnosis,
    setIsDiagnosisPopupOpen,
    isDiagnosisPopupOpen,
    showBirthWarningModal,
    setShowBirthWarningModal,
    cowMother,
    isSavingDiagnosis
  } = breedingListDetails

  const {
    savedBirthAnimals,
    useDateInterval,
    hasBirthAnimalsFromDiagnosis,
    diagnosisStatus,
    diagnosisDate,
    hasJustSaved,
    handleToggleDateInterval,
    handleSaveMultipleBirths,
    handleRemoveBirth,
    handleSaveAll
  } = useBreedingListDetailsForm({
    pregnancyDiagnosis,
    handleSave,
    handleSaveDiagnosis,
    handleSaveDiagnosisComplete
  })

  const { isHistoryModalOpen, openHistoryModal, closeHistoryModal } = useHistoryDiagnosisModal()

  const {
    watch,
    setValue,
    formState: { isDirty }
  } = useFormContext<formParams>()

  useEffect(() => {
    if (isDiagnosisPopupOpen && !pregnancyDiagnosis?.diagnostico) {
      setValue('pregnancyDiagnosis.diagnostico', 'prenha', { shouldDirty: false })
      setShowDiagnosisForm(true)
    }
  }, [isDiagnosisPopupOpen, pregnancyDiagnosis?.diagnostico, setValue])

  useEffect(() => {
    if (useDateInterval) {
      const currentDataInicio = watch('coverageProcess.dataInicio')
      const currentDataFim = watch('coverageProcess.dataFim')

      if (!currentDataInicio) {
        const beginDate = new Date()
        beginDate.setDate(beginDate.getDate() - 1)
        setValue('coverageProcess.dataInicio', beginDate.toISOString(), { shouldDirty: false })
      }
      if (!currentDataFim) {
        setValue('coverageProcess.dataFim', new Date().toISOString(), { shouldDirty: false })
      }
    }
  }, [useDateInterval, setValue])

  const hasDiagnosis = !!(pregnancyDiagnosis?.diagnostico || pregnancyDiagnosis?.dataDiagnostico)

  const isNewDiagnosis = !pregnancyDiagnosis?.diagnostico
  const currentDiagnosisStatus = watch('pregnancyDiagnosis.diagnostico')

  const shouldShowSaveButton =
    !hasJustSaved &&
    (currentDiagnosisStatus === 'prenha'
      ? savedBirthAnimals.length > 0 || (!isNewDiagnosis && isDirty)
      : currentDiagnosisStatus === 'vazia' && (isNewDiagnosis || isDirty))

  return (
    <>
      <BreedingHeader />

      {isLoading ? (
        <div className={style.loadingContainer}>
          <Loading />
        </div>
      ) : (
        <>
          <h1 className={style.title}>Reprodução</h1>
          <div className={style.propertiesContainer}>
            <AnimalInfoSection cowMother={cowMother} onOpenHistory={openHistoryModal} />

            <form>
              <div className={style.reproductionDiagnosisLayout}>
                <ReproductionSection
                  useDateInterval={useDateInterval}
                  handleToggleDateInterval={handleToggleDateInterval}
                />

                <DiagnosisSection
                  hasDiagnosis={hasDiagnosis}
                  showDiagnosisForm={showDiagnosisForm}
                  setShowDiagnosisForm={setShowDiagnosisForm}
                  hasBirthAnimalsFromDiagnosis={hasBirthAnimalsFromDiagnosis}
                  diagnosisStatus={diagnosisStatus}
                  diagnosisDate={diagnosisDate}
                  savedBirthAnimals={savedBirthAnimals}
                  pregnancyDiagnosis={pregnancyDiagnosis}
                  handleOpenDiagnosisModal={handleOpenDiagnosisModal}
                  handleRemoveBirth={handleRemoveBirth}
                  hasJustSaved={hasJustSaved}
                />
              </div>

              <div className={style.actionsContainer}>
                <Button
                  type='button'
                  className={`${style.buttonWrapper} ${style.buttonOutlined}`}
                  onClick={handleOpenDeleteModal}
                  outlined
                >
                  Excluir
                </Button>

                {shouldShowSaveButton && (
                  <Button
                    isLoading={isSavingDiagnosis || isUpdatingCoverageProcess}
                    disabled={isSavingDiagnosis || isUpdatingCoverageProcess}
                    type='button'
                    className={style.buttonWrapper}
                    onClick={handleSaveAll}
                  >
                    Salvar
                  </Button>
                )}
              </div>
            </form>
          </div>
        </>
      )}

      <ReproductiveHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={closeHistoryModal}
        animalId={pregnancyDiagnosis?.idAnimal!}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        title='Confirmar exclusão'
        isLoading={isUpdatingCoverageProcess}
        message='Tem certeza que deseja remover este registro? Esta ação não pode ser desfeita.'
        confirmText='Sim, remover'
        cancelText='Cancelar'
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteModal}
      />

      <DiagnosisModal
        isOpen={isDiagnosisPopupOpen}
        onClose={() => setIsDiagnosisPopupOpen(false)}
        motherAnimalId={pregnancyDiagnosis?.idAnimal || ''}
        motherAnimalName={pregnancyDiagnosis?.nomeVaca || pregnancyDiagnosis?.idAnimal || 'Animal'}
        fatherAnimalId={pregnancyDiagnosis?.touro || ''}
        diagnosisDate={diagnosisDate}
        reproductionType={
          montaTypeOptions.find((opt) => opt.id === pregnancyDiagnosis?.tipoReproducao)?.value || 'Monta Natural'
        }
        reproductionDate={watch('pregnancyDiagnosis.dataInicial')}
        onSaveAll={handleSaveMultipleBirths}
        existingBirths={savedBirthAnimals}
      />

      <ConfirmationModal
        isOpen={showBirthWarningModal}
        title='Atenção'
        message='O animal está fora da data prevista para o parto. Deseja prosseguir com o cadastro mesmo assim?'
        confirmText='Sim, prosseguir'
        cancelText='Não, cancelar'
        onConfirm={handleConfirmOpenDiagnosisModal}
        onCancel={() => setShowBirthWarningModal(false)}
      />
    </>
  )
}
